// ---- Wear-filler body scaling (pure, THREE-free) ----
// THE shared baseline for "how big is the body under the gear?". Wear-slot
// prim fillers (content/parts docs on torsowear/legwear*/footwear*/back) are
// AUTHORED against the AVERAGE MALE frame; at fill time buildFromDoc scales
// their radial dimensions by the factors here so the same doc clears a stout
// or female body. Three consumers, one home — the fill (scaleWearPart), the
// check thresholds (wear-check envelopeWarns), and the rig-measured seat
// (src/wear/seat.ts wearBodyFromRig) — a drift-guard test asserts the formula
// numbers here agree with the measured rig, so they can never diverge silently.
//
// Factors are UNIFORM per region and clamped ≥ 1: average-male authoring is
// the FLOOR (smaller bodies wear the authored size loose; only bigger bodies
// grow the shell). wearScaleCtx returns null when every factor is identity —
// callers skip the transform entirely, so the average-male build is
// byte-identical by construction (the snapshot guard's contract).
//
// The one non-radial case is the female BUST: her chest profile is NARROWER
// than the male's, but the body's front-only `frontBulge` pushes the +z face
// proud of the male chest — a male-snug breastplate clips it. scaleWearPart
// merges the body's bust bulge into torsowear LATHES (the same mechanism the
// body itself uses), converted into each lathe's local y / pre-depth z space.

import { frameScales, HUMANOID_TUNING, PELVIS_HIP_Y } from "./humanoid.ts";
import { WEAR_MODEL_SLOTS, type Part } from "./parts.ts";
import type { BodyBuild, Sex, WearSlot } from "../shared.ts";

/** Wear regions that receive radial scaling at fill time. */
export type WearRegion = "torso" | "leg" | "foot" | "back";

/** Paperdoll wear slot → scale region (head never scales — headwear has its
 *  own crown-seat system in head-seat.ts). */
export const REGION_BY_WEAR_SLOT: Partial<Record<WearSlot, WearRegion>> = {
  torso: "torso",
  legs: "leg",
  feet: "foot",
  back: "back",
};

/** Which humanoid MODEL slots scale, and as what region — DERIVED from
 *  WEAR_MODEL_SLOTS (parts.ts, the one home for slot names) so a slot rename
 *  flows here automatically. Decorative doc.parts, drop models
 *  (partGroundBuilder), headwear/face/waist slots, and structure parts NEVER
 *  scale — only the paperdoll body slots (+ back). */
export const WEAR_SCALE_REGIONS: Record<string, WearRegion> = Object.fromEntries(
  (Object.keys(WEAR_MODEL_SLOTS) as WearSlot[]).flatMap((ws) => {
    const region = REGION_BY_WEAR_SLOT[ws];
    return region ? WEAR_MODEL_SLOTS[ws].map((slot) => [slot, region] as const) : [];
  }),
);

export type WearScaleCtx = {
  /** Radial (x/z) multiplier per region ON TOP of `all` — every factor ≥ 1. */
  torso: number;
  leg: number;
  foot: number;
  back: number;
  /** Shoulder-pad factor: parts tagged limb shoulderL/R inside a torsowear
   *  filler ride the ARM, which grows at the limb rate (≠ the torso rate on
   *  stout) — the same rate envelopeWarns judges them against (armR). */
  arm: number;
  /** UNIFORM similarity factor from the doc's body.scale (1 = baseline). A
   *  scaled humanoid grows every body dimension ∝ scale, so its wear fillers
   *  scale x, y, z AND radii by this — unlike the radial build factors, it
   *  applies in BOTH directions (a small body shrinks its gear). */
  all: number;
  /** Female bust pass-through for torsowear lathes: [y, dz] pairs where y is
   *  TORSO-ANCHOR-local (world units) and dz is the WORLD front push (the
   *  body's bulge × its depth squash). scaleWearPart re-bases y to each
   *  lathe's baseY and divides dz by the lathe's own `depth`. null on male. */
  bust: readonly (readonly [number, number])[] | null;
};

/** Nominal torso-anchor height (hip 1.06 + waist 0.24 + chest/2 0.17) — the
 *  legLen preset cancels out of the anchor-local conversion, so this constant
 *  works for every build (see the derivation in wearScaleCtx). The BACK
 *  anchor shares this y (humanoid.ts publishes both at the chest midline). */
export const TORSO_ANCHOR_NOMINAL_Y = PELVIS_HIP_Y + 0.24 + 0.17;

/**
 * Formula scale factors for a body. Returns null when every factor is
 * identity (average male at any scale) — the byte-identical fast path.
 */
export function wearScaleCtx(
  sex: Sex | undefined,
  build: BodyBuild | undefined,
  scale?: number,
): WearScaleCtx | null {
  const { P, female, armScale, legScale } = frameScales(sex, build);
  const all = scale ?? 1;
  const s = all * 1.1;

  // Torso width/depth grow together on the presets; take the larger so a
  // squat torso (w 1.1 / d 1.08) still clears on both axes. Female profile is
  // NARROWER (a profile swap, not a multiplier) → clamps to 1.
  const torso = Math.max(1, P.torsoW, P.depth);
  // Limb capsules: preset limbR × the female slimming (clamps to 1).
  const leg = Math.max(1, legScale);
  // The body's boot never build-scales, but the cuff wraps the SCALED shin —
  // and a uniformly chunkier stout boot reads right. Track the leg factor.
  const foot = leg;
  const back = torso;
  // Shoulder pads ride the ARM (limb rate), not the torso rate.
  const arm = Math.max(1, armScale);

  // Bust: body pairs are [nominal y, dz] (×s at build; dz squashed by the
  // torso lathe's mesh scale.z = torsoDepth·P.depth). Anchor-local y:
  //   anchorY = hipY + 0.41·s = 1.06·s·legLen + 0.41·s
  //   bustWorldY = y·s + dy  where dy = hipY − 1.06·s
  //   → local = (y − 1.47)·s   (legLen cancels)
  const bust = female
    ? HUMANOID_TUNING.female.bust.map(
        ([y, dz]) =>
          [(y - TORSO_ANCHOR_NOMINAL_Y) * s, dz * s * HUMANOID_TUNING.torsoDepth * P.depth] as const,
      )
    : null;

  if (all === 1 && torso === 1 && leg === 1 && foot === 1 && back === 1 && arm === 1 && !bust) {
    return null;
  }
  return { torso, leg, foot, back, arm, all, bust };
}

// ---------------------------------------------------------------------------
// Part transform
// ---------------------------------------------------------------------------

type Pair = readonly (readonly [number, number])[];

/** Evaluate a frontBulge polyline with meshkit's endpoint-CLAMP semantics
 *  (pushLatheFront: below the first y → first dz, above the last → last dz). */
export function bulgeAt(bulge: Pair, y: number): number {
  const first = bulge[0]!;
  const last = bulge[bulge.length - 1]!;
  if (y <= first[0]) return first[1];
  if (y >= last[0]) return last[1];
  for (let i = 1; i < bulge.length; i++) {
    const [y1, d1] = bulge[i]!;
    if (y <= y1) {
      const [y0, d0] = bulge[i - 1]!;
      return d0 + (d1 - d0) * ((y - y0) / (y1 - y0 || 1));
    }
  }
  return last[1];
}

/** Sum two clamp-semantics polylines at the union of their breakpoints —
 *  exact for piecewise-linear + clamp (the sum is linear between union
 *  breakpoints and constant beyond the outermost ones). */
function mergeBulges(a: Pair, b: Pair): [number, number][] {
  const ys = [...new Set([...a.map(([y]) => y), ...b.map(([y]) => y)])].sort((p, q) => p - q);
  return ys.map((y) => [y, bulgeAt(a, y) + bulgeAt(b, y)]);
}

/** [x·fxz, y·fy, z·fxz] — radial factor on x/z, the uniform factor on y. */
const scaleVec = (v: [number, number, number], fxz: number, fy: number): [number, number, number] => [
  v[0] * fxz,
  v[1] * fy,
  v[2] * fxz,
];

/**
 * Pure similarity transform about the slot's anchor axis. `ctx.all` (the
 * doc's body.scale) scales EVERY dimension uniformly — the body it dresses
 * grew the same way; the region's radial factor then multiplies x/z offsets
 * + radii on top (vertical seating stays anchor-based, so build factors never
 * touch y). Parts tagged limb shoulderL/R in the torso region take ctx.arm
 * (they ride the arm, which grows at the limb rate). For the torso region,
 * merges the ctx bust bulge into lathes. Returns the SAME object when nothing
 * changes.
 */
export function scaleWearPart(part: Part, region: WearRegion, ctx: WearScaleCtx): Part {
  const shoulderPad =
    region === "torso" && (part.limb === "shoulderL" || part.limb === "shoulderR");
  const u = ctx.all;
  const f = u * (shoulderPad ? ctx.arm : ctx[region]);
  const bust = region === "torso" && part.prim === "lathe" ? ctx.bust : null;
  if (f === 1 && u === 1 && !bust) return part;

  const out = { ...part } as Part & { repeat?: Part["repeat"] };
  if (out.repeat) {
    out.repeat = { ...out.repeat };
    if (out.repeat.step) out.repeat.step = scaleVec(out.repeat.step, f, u);
    if (out.repeat.ring) out.repeat.ring = { r: out.repeat.ring.r * f };
  }

  if (out.prim === "rod" || out.prim === "bone") {
    out.from = scaleVec(out.from, f, u);
    out.to = scaleVec(out.to, f, u);
    if (out.prim === "rod") out.r *= f;
    else {
      out.r0 *= f;
      out.r1 *= f;
    }
    return out;
  }

  out.at = scaleVec(out.at, f, u);
  switch (out.prim) {
    case "box":
      out.size = [out.size[0] * f, out.size[1] * u, out.size[2] * f];
      break;
    case "blob":
    case "ball":
      out.r *= f;
      break;
    case "cone":
      out.r *= f;
      out.h *= u;
      break;
    case "cylinder":
      out.rTop *= f;
      out.rBottom *= f;
      out.h *= u;
      break;
    case "tube":
      // len runs along the axis (x or z) — radial in the xz plane either way.
      out.r *= f;
      out.len *= f;
      break;
    case "arc":
      out.r *= f;
      out.tube *= f;
      break;
    case "lathe": {
      out.profile = out.profile.map(([r, y]) => [r * f, y * u] as [number, number]);
      let bulgeGeom = out.frontBulge?.map(([y, dz]) => [y * u, dz * f] as [number, number]);
      if (bust) {
        // ctx.bust y is torso-anchor-local WORLD (∝ scale·1.1, same as the
        // now-uniformly-scaled filler); the lathe's profile/bulge y-space is
        // relative to its baseY (= anchor + scaled at[1]). dz is WORLD; the
        // mesh's scale.z = depth squashes geometry z, so divide.
        const depth = out.depth ?? 1;
        const local = bust.map(
          ([y, dz]) => [y - out.at[1], dz / depth] as [number, number],
        );
        bulgeGeom = bulgeGeom?.length ? mergeBulges(bulgeGeom, local) : local;
      }
      if (bulgeGeom) out.frontBulge = bulgeGeom;
      break;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Formula body envelope — the authored-space baseline the factors multiply.
// ---------------------------------------------------------------------------

export type WearBodyRadii = {
  /** Max torso lathe radius in the CHEST band (x half-width, world units). */
  chestR: number;
  /** Front (+z) extent in the chest band — bust included on female. */
  chestZ: number;
  /** Max torso lathe radius in the HIP band. */
  hipR: number;
  /** Max thigh capsule radius (belly swell included). */
  thighR: number;
  /** Max shin capsule radius (calf swell included). */
  shinR: number;
  /** Shin/ankle radius at the boot cuff (ankle bridge included). */
  ankleR: number;
  /** Knee pivot y in HIP-local space (negative — leg hangs −Y). */
  kneeY: number;
  /** Ankle y in KNEE-local space (negative). */
  ankleY: number;
  /** Boot heel→toe z extent (caps included). */
  footLen: number;
  /** Max upper-arm radius (parity with sleeve seat armR). */
  armR: number;
};

/** Max radius of a tapered capsule with an optional mid `bulge` swell. */
function capsuleMaxR(
  r0: number,
  r1: number,
  belly?: { amount: number; at: number },
): number {
  let max = Math.max(r0, r1);
  if (belly) {
    const mid = (r0 + (r1 - r0) * belly.at) * (1 + belly.amount);
    if (mid > max) max = mid;
  }
  return max;
}

/** Max profile radius within a y band (inclusive). Profile rows are [r, y];
 *  shared with wear-check's latheBandR (pass band edges pre-shifted). */
export function bandMaxR(
  profile: readonly (readonly [number, number])[],
  y0: number,
  y1: number,
): number {
  let max = 0;
  for (const [r, y] of profile) {
    if (y >= y0 && y <= y1 && r > max) max = r;
  }
  return max;
}

/** Max frontBulge dz WITHIN a y band, under meshkit's endpoint-clamp
 *  semantics: the bulge is piecewise-linear between pairs and constant beyond
 *  the outermost ones, so the band max is the max of the band-edge values and
 *  any interior pairs. Used by wear-check's chest front-z gate so a bulge
 *  authored at belly level can't inflate the chest reading. */
export function bulgeMaxInBand(bulge: Pair, y0: number, y1: number): number {
  let max = Math.max(bulgeAt(bulge, y0), bulgeAt(bulge, y1));
  for (const [y, dz] of bulge) {
    if (y >= y0 && y <= y1 && dz > max) max = dz;
  }
  return max;
}

/** Chest band in NOMINAL profile y (the chest bulge → shoulder shelf rows). */
export const CHEST_BAND_NOMINAL: readonly [number, number] = [1.42, 1.632];
/** Hip band in nominal profile y (the hip round-under rows). */
export const HIP_BAND_NOMINAL: readonly [number, number] = [0.94, 1.08];

/**
 * Formula body envelope for any sex/build/scale — derived from the same
 * HUMANOID_TUNING numbers the builder uses. The average-male values are the
 * AUTHORING BASELINE for every wear-filler doc; the drift-guard test pins
 * these against the rig-measured wearBodyFromRig (src/wear/seat.ts).
 */
export function wearBodyRadii(
  sex?: Sex,
  build?: BodyBuild,
  scale?: number,
): WearBodyRadii {
  const T = HUMANOID_TUNING;
  const s = (scale ?? 1) * 1.1;
  // frameScales is the builder's own derivation (humanoid.ts) — shared, so a
  // preset-rule change there reaches this baseline automatically.
  const { P, female, armScale, legScale, torsoProfile: profile } = frameScales(sex, build);
  const depthScale = T.torsoDepth * P.depth;

  const chestProfR = bandMaxR(profile, CHEST_BAND_NOMINAL[0], CHEST_BAND_NOMINAL[1]);
  const chestR = chestProfR * s * P.torsoW;
  const bustDz = female ? Math.max(...T.female.bust.map(([, dz]) => dz)) : 0;
  // World z = (geometry radius·torsoW + bulge dz)·s × the mesh depth squash.
  const chestZ = (chestProfR * P.torsoW + bustDz) * s * depthScale;
  const hipR = bandMaxR(profile, HIP_BAND_NOMINAL[0], HIP_BAND_NOMINAL[1]) * s * P.torsoW;

  const thighR = capsuleMaxR(T.thigh[0], T.thigh[1], T.belly.thigh) * s * legScale;
  const shinR = capsuleMaxR(T.shin[0], T.shin[1], T.belly.shin) * s * legScale;
  // Shin lower cap scales with the leg; the boot's ankle bridge does not.
  const ankleR = Math.max(T.shin[1] * legScale, T.boot.ankleBotR) * s;

  const kneeY = (0.58 - PELVIS_HIP_Y) * s * P.legLen;
  const ankleY = (T.ankleY - 0.58 * P.legLen) * s;
  const footLen = (T.boot.toeZ - T.boot.heelZ + T.boot.heelR + T.boot.toeR) * s;

  const armR = capsuleMaxR(T.upperArm[0], T.upperArm[1], T.belly.upperArm) * s * armScale;

  return { chestR, chestZ, hipR, thighR, shinR, ankleR, kneeY, ankleY, footLen, armR };
}

/** Pinned average-male (s=1.1) baseline — THE numbers wear fillers author to. */
export const AVERAGE_MALE_BODY_RADII: WearBodyRadii = wearBodyRadii();
